/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBadges } from '@/lib/proToolkit';

const TIER_STYLE = {
  bronze: 'from-amber-700 to-amber-900 text-amber-100',
  silver: 'from-slate-400 to-slate-600 text-white',
  gold: 'from-yellow-400 to-yellow-600 text-yellow-950',
  platinum: 'from-cyan-300 to-blue-500 text-white',
};

const BadgeCard = ({ badge }) => {
  const tierClass = TIER_STYLE[badge.tier] || TIER_STYLE.bronze;
  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all',
        badge.awarded ? 'shadow-elev-md hover:shadow-elev-lg' : 'opacity-60 grayscale',
      )}
      data-testid={`badge-card-${badge.id}`}
    >
      <div className={cn('h-2 w-full bg-gradient-to-r', tierClass)} />
      <CardContent className="p-4 text-center space-y-2">
        <div className="text-5xl" role="img" aria-label={badge.title}>
          {badge.awarded ? badge.emoji : <Lock className="h-10 w-10 mx-auto text-muted-foreground" />}
        </div>
        <div className="font-display font-bold text-sm">{badge.title}</div>
        <p className="text-[11px] text-muted-foreground leading-snug">{badge.description}</p>
        <Badge variant="secondary" className="text-[9px] uppercase tracking-wider">
          {badge.tier}
        </Badge>
      </CardContent>
      {badge.awarded && (
        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <Trophy className="h-3.5 w-3.5" />
        </div>
      )}
    </Card>
  );
};

export const BadgesGallery = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await getBadges();
        setData(d);
      } catch {
        setData({ awarded_count: 0, total_count: 0, badges: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={`bg-sk-${i}`} className="h-52 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const pct = data.total_count > 0 ? Math.round((data.awarded_count / data.total_count) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="badges-gallery">
      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary-gradient text-primary-foreground flex items-center justify-center shadow-glow">
            <Trophy className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">
              {data.awarded_count} / {data.total_count} conquistati
            </div>
            <Progress value={pct} className="h-2 mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              Sblocca nuovi badge continuando la tua attivita sulla piattaforma.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {data.badges.map((b) => (
          <BadgeCard key={b.id} badge={b} />
        ))}
      </div>
    </div>
  );
};

export default BadgesGallery;
